import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { Member } from '../../types';

interface MemberDemographicsChartProps {
  members: Member[];
  chartType?: 'pie' | 'bar';
  height?: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export const MemberDemographicsChart: React.FC<MemberDemographicsChartProps> = ({
  members,
  chartType = 'pie',
  height = 300
}) => {
  // Calculate demographics data
  const genderData = React.useMemo(() => {
    const genderCounts = members.reduce((acc, member) => {
      const gender = member.gender || 'Unknown';
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(genderCounts).map(([gender, count]) => ({
      name: gender,
      value: count,
      percentage: ((count / members.length) * 100).toFixed(1)
    }));
  }, [members]);

  const ageGroupData = React.useMemo(() => {
    const ageCounts = members.reduce((acc, member) => {
      const ageGroup = member.ageGroup || 'Unknown';
      acc[ageGroup] = (acc[ageGroup] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(ageCounts).map(([ageGroup, count]) => ({
      name: ageGroup,
      value: count,
      percentage: ((count / members.length) * 100).toFixed(1)
    }));
  }, [members]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label || data.name}`}</p>
          <p className="text-blue-600">{`Count: ${data.value}`}</p>
          <p className="text-gray-600">{`Percentage: ${data.payload.percentage}%`}</p>
        </div>
      );
    }
    return null;
  };

  if (chartType === 'bar') {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">Gender Distribution</h3>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={genderData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Age Group Distribution</h3>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={ageGroupData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill="#00C49F" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-semibold mb-4 text-center">Gender Distribution</h3>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={genderData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percentage }) => `${name}: ${percentage}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {genderData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4 text-center">Age Group Distribution</h3>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={ageGroupData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percentage }) => `${name}: ${percentage}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {ageGroupData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};